import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { store } from '../../Infrastructure/store';
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { getMsalInstance } from '../../Infrastructure/Security/msalInstance';
import { unauthorize } from '../../Infrastructure/Security/authorizationSlice';

let cachedBaseUrl: string | undefined;
let cachedRawBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> | undefined;

/**
 * fetchBaseQuery is cached rather than rebuilt per request. baseUrl must stay on the instance so that
 * RTK Query's own url joining runs — many endpoints define paths without a leading slash.
 */
function getRawBaseQuery(baseUrl: string) {
  if (!cachedRawBaseQuery || cachedBaseUrl !== baseUrl) {
    cachedBaseUrl = baseUrl;
    cachedRawBaseQuery = fetchBaseQuery({ baseUrl });
  }

  return cachedRawBaseQuery;
}

/**
 * Creating a Custom FetchBaseQuery implementation so that we can access AppSettings and MSAL instance to configure the API.
 */
const companyBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, triniumApi, extraOptions) => {
  const msalInstance = getMsalInstance();
  const appSettings = store.getState().appSettings;
  const account = msalInstance.getActiveAccount();

  if (!appSettings || !account) {
    throw new Error('AppSettings or Active Account missing during API execution.');
  }

  const accessTokenRequest: SilentRequest = {
    scopes: [appSettings.azure.scope],
    account: account
  };

  const token = await msalInstance.acquireTokenSilent(accessTokenRequest).catch((error: unknown) => {
    // MSAL will attempt to retrieve an access token silently but if that fails this is sending them back to the login
    if (error instanceof InteractionRequiredAuthError) {
      return msalInstance.acquireTokenRedirect({
        scopes: accessTokenRequest.scopes
      });
    } else {
      throw error;
    }
  });

  // The token is injected into the request args rather than prepareHeaders because a cached
  // fetchBaseQuery instance cannot close over a per-request token.
  const adjustedArgs: FetchArgs = typeof args === 'string' ? { url: args } : { ...args };

  const headers = new Headers(adjustedArgs.headers as HeadersInit | undefined);
  headers.set('Authorization', `Bearer ${token?.accessToken ?? 'Missing Access Token'}`);
  adjustedArgs.headers = headers;

  const result = await getRawBaseQuery(appSettings.triniumApiBaseUrl)(adjustedArgs, triniumApi, extraOptions);

  // If we ever get a 401 back we want to update our state
  if (result.error && result.error.status === 401) {
    store.dispatch(unauthorize());
  }

  return result;
};

export const triniumApi = createApi({
  reducerPath: 'triniumApi',
  baseQuery: companyBaseQuery,
  tagTypes: [
    'charges',
    'consignment',
    'dispatch',
    'dispatch-by-job',
    'dispatch-by-search',
    'drivers',
    'edi',
    'legs',
    'payments',
    'tractors',
    'export-voyages',
    'import-voyages'
  ],
  // We are using code-splitting practices and endpoints are defined in individual files
  endpoints: () => ({})
});
